import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import {
  AlertTriangle, Calendar, Car as CarIcon, Clock, FileText, Phone,
  ShieldCheck, Wrench, TrendingUp, ChevronRight, MessageCircle, Mail, RefreshCw,
} from "lucide-react";
import { format, parseISO } from "date-fns";

/**
 * Morning operations dashboard — single source of truth for the day:
 *  - Today's pickups
 *  - Today's returns
 *  - Overdue rentals (in red)
 *  - Compliance alerts: insurance/MA inspection/registration expiring ≤30d
 *  - Maintenance due in the next 7 days
 *  - Yesterday recap (revenue, payment count) + 7-day rolling
 */
export default function Today() {
  const { t, fmtCurrency } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ops/today");
      setData(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (!data) return <div className="overline">{t("common.loading")}</div>;

  const totalAlerts =
    data.overdue.length +
    data.compliance.insurance.length +
    data.compliance.ma_inspection.length +
    data.compliance.registration.length +
    data.maintenance_due_7d.length;

  return (
    <div className="space-y-8" data-testid="today-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">{t("today.pageTitle")}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            {format(new Date(), "EEEE, MMM d")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t("today.subtitle")}</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 border border-zinc-300 hover:border-[#F5A623] hover:text-[#D97706] px-3 py-2 text-xs"
          data-testid="today-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> {t("today.refresh")}
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-zinc-200">
        <Kpi icon={CarIcon} label={t("today.kpis.pickupsToday")} value={data.pickups_today.length} testid="kpi-pickups" />
        <Kpi icon={Calendar} label={t("today.kpis.returnsToday")} value={data.returns_today.length} testid="kpi-returns" />
        <Kpi icon={AlertTriangle} label={t("today.kpis.overdue")} value={data.overdue.length} tone={data.overdue.length > 0 ? "alert" : "default"} testid="kpi-overdue" />
        <Kpi icon={ShieldCheck} label={t("today.kpis.alerts")} value={totalAlerts} tone={totalAlerts > 0 ? "warning" : "success"} testid="kpi-alerts" />
      </div>

      {/* Two-column main area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Returns today */}
        <Section title={t("today.returnsToday")} icon={Calendar} count={data.returns_today.length} accent="orange">
          {data.returns_today.length === 0 ? <Empty text={t("today.empty.returns")} /> : (
            data.returns_today.map((r) => <RentalRow key={r.id} r={r} accent="orange" />)
          )}
        </Section>

        {/* Overdue */}
        <Section title={t("today.overdue")} icon={AlertTriangle} count={data.overdue.length} accent="red">
          {data.overdue.length === 0 ? <Empty text={t("today.empty.overdue")} /> : (
            data.overdue.map((r) => <RentalRow key={r.id} r={r} accent="red" extraText={`${r.overdue_hours}h late`} />)
          )}
        </Section>

        {/* Pickups today */}
        <Section title={t("today.pickupsToday")} icon={CarIcon} count={data.pickups_today.length} accent="black">
          {data.pickups_today.length === 0 ? <Empty text={t("today.empty.pickups")} /> : (
            data.pickups_today.map((r) => <RentalRow key={r.id} r={r} accent="black" />)
          )}
        </Section>

        {/* Compliance — insurance / inspection / registration */}
        <Section title={t("today.compliance")} icon={ShieldCheck} count={
          data.compliance.insurance.length + data.compliance.ma_inspection.length + data.compliance.registration.length
        } accent="amber">
          {[
            ...data.compliance.insurance.map((c) => ({ ...c, kind: "insurance" })),
            ...data.compliance.ma_inspection.map((c) => ({ ...c, kind: "inspection" })),
            ...data.compliance.registration.map((c) => ({ ...c, kind: "registration" })),
          ].sort((a, b) => a.days_left - b.days_left).slice(0, 12).map((c, i) => (
            <ComplianceRow key={`${c.car_id}-${c.kind}-${i}`} item={c} t={t} />
          ))}
          {data.compliance.insurance.length + data.compliance.ma_inspection.length + data.compliance.registration.length === 0 && (
            <Empty text={t("today.empty.compliance")} />
          )}
        </Section>
      </div>

      {/* Maintenance + Recap */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Section title={t("today.maintenance")} icon={Wrench} count={data.maintenance_due_7d.length} accent="blue">
            {data.maintenance_due_7d.length === 0 ? <Empty text={t("today.empty.maintenance")} /> : (
              data.maintenance_due_7d.map((m, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-zinc-100 last:border-b-0">
                  <div>
                    <div className="font-mono text-sm font-semibold">{m.car?.model} · {m.car?.plate}</div>
                    <div className="text-xs text-zinc-500">{m.type || m.description}</div>
                  </div>
                  <div className="text-xs font-mono text-blue-700">{format(parseISO(m.scheduled_date), "MMM d")}</div>
                </div>
              ))
            )}
          </Section>
        </div>
        <Section title={t("today.recap")} icon={TrendingUp} accent="green">
          <div className="p-4 space-y-4">
            <div>
              <div className="overline">{t("today.yesterdayRevenue")}</div>
              <div className="font-display font-black text-3xl tracking-tighter text-green-700">{fmtCurrency(data.recap.yesterday_revenue)}</div>
              <div className="text-xs text-zinc-500">{data.recap.yesterday_payments_count} {t("today.payments")}</div>
            </div>
            <div className="pt-3 border-t border-zinc-100">
              <div className="overline">{t("today.weekRevenue")}</div>
              <div className="font-display font-black text-2xl tracking-tighter">{fmtCurrency(data.recap.week_revenue)}</div>
              <div className="text-xs text-zinc-500">{t("today.last7days")}</div>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "text-zinc-900",
    alert: "text-red-700",
    warning: "text-amber-700",
    success: "text-green-700",
  };
  return (
    <div className="border-r border-b border-zinc-200 -ml-px -mt-px p-5 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className={`font-display font-black text-3xl tracking-tighter ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, accent, children }) {
  const accentBg = {
    orange: "bg-orange-50 border-[#F5A623]/30 text-[#D97706]",
    red:    "bg-red-50 border-red-200 text-red-700",
    black:  "bg-zinc-900 border-zinc-900 text-white",
    amber:  "bg-amber-50 border-amber-200 text-amber-800",
    blue:   "bg-blue-50 border-blue-200 text-blue-800",
    green:  "bg-green-50 border-green-200 text-green-800",
  }[accent];
  return (
    <div className="border border-zinc-200 bg-white">
      <div className={`px-4 py-3 border-b flex items-center justify-between ${accentBg}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <div className="overline">{title}</div>
        </div>
        {count !== undefined && <div className="font-mono text-xs">{count}</div>}
      </div>
      <div className="divide-y divide-zinc-100">{children}</div>
    </div>
  );
}

function RentalRow({ r, accent, extraText }) {
  const car = r.car || {};
  const cu = r.customer || {};
  return (
    <Link
      to="/alugueis"
      className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-zinc-50"
      data-testid={`today-rental-${r.id}`}
    >
      <div className="min-w-0">
        <div className="font-mono text-sm font-semibold truncate">
          {car.model} · <span className="text-zinc-500">{car.plate}</span>
        </div>
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <span>{cu.name}</span>
          {cu.phone && (
            <a
              onClick={(e) => e.stopPropagation()}
              href={`tel:${cu.phone}`}
              className="inline-flex items-center gap-1 text-zinc-400 hover:text-[#F5A623]"
            >
              <Phone className="w-3 h-3" />{cu.phone}
            </a>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {extraText && <span className={`text-[10px] font-mono uppercase ${accent === "red" ? "text-red-600" : "text-zinc-500"}`}>{extraText}</span>}
        <ChevronRight className="w-4 h-4 text-zinc-400" />
      </div>
    </Link>
  );
}

function ComplianceRow({ item, t }) {
  const labels = {
    insurance: t("today.compliance_kind.insurance"),
    inspection: t("today.compliance_kind.inspection"),
    registration: t("today.compliance_kind.registration"),
  };
  const expired = item.days_left < 0;
  const urgent = item.days_left <= 7 && item.days_left >= 0;
  return (
    <Link to="/frota" className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-zinc-50">
      <div className="min-w-0">
        <div className="font-mono text-sm font-semibold truncate">{item.model} · <span className="text-zinc-500">{item.plate}</span></div>
        <div className="text-xs text-zinc-500">{labels[item.kind]} · {item.expires_at}</div>
      </div>
      <div className="shrink-0">
        <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${
          expired ? "bg-red-100 text-red-700 border-red-300" :
          urgent ? "bg-amber-100 text-amber-800 border-amber-300" :
          "bg-zinc-100 text-zinc-700 border-zinc-200"
        }`}>
          {expired ? `${Math.abs(item.days_left)}d ${t("today.expiredAgo")}` : `${item.days_left}d ${t("today.left")}`}
        </span>
      </div>
    </Link>
  );
}

const Empty = ({ text }) => <div className="px-4 py-6 text-center text-xs text-zinc-400">{text}</div>;
